import React, { useEffect, useState } from 'react';
import { format, formatDistance } from 'date-fns';
import Breadcrumb from '../../components/Breadcrumb';
import Page from '../../components/Page';

import EntitySearchForm from '../../components/EntitySearchForm';
import Pagination from '../../components/Pagination';
import UserAvatar from '../../components/UserAvatar';
import client from '../../lib/feathers';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const ACCESS_LEVELS = ['free', 'taster', 'collector', 'enthusiast', 'connoisseur', 'vip'];

const ACCESS_BADGE_COLORS = {
  free: 'bg-gray-100 text-gray-700',
  taster: 'bg-blue-50 text-blue-700',
  collector: 'bg-purple-50 text-purple-700',
  enthusiast: 'bg-pink-50 text-pink-700',
  connoisseur: 'bg-amber-50 text-amber-700',
  vip: 'bg-red-50 text-red-700',
};

function TierBadge({ tier }) {
  if (!tier) return null;
  const cls = ACCESS_BADGE_COLORS[tier] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${cls}`}>
      {tier}
    </span>
  );
}

function isOverrideActive(override) {
  if (!override || !override.level) return false;
  if (!override.expiresAt) return true;
  return new Date(override.expiresAt) > new Date();
}

export default function Users() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;
  const [page, setPage] = useState(parseInt(query.get('page'), 10) || 0);
  const [roleFilter, setRoleFilter] = useState();
  const [accessLevelFilter, setAccessLevelFilter] = useState(query.get('accessLevel') || '');
  const [hasOverrideFilter, setHasOverrideFilter] = useState(false);
  const roleList = ['user', 'industry', 'admin'];


  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [keywords, setKeywords] = useState(query.keywords);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const getUsers = async () => {
    try {
      const service = client.service('admin/user');
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: {
          createdAt: -1,
        },
      };

      if (keywords && keywords.length) {
        q.$or = [
          { username: { $iLike: `%${keywords}%` } },
          { firstName: { $iLike: `%${keywords}%` } },
          { lastName: { $iLike: `%${keywords}%` }
        }];
      }

      if ( roleFilter ) {
        q.role = roleFilter;
      }

      if (accessLevelFilter) {
        q.accessLevel = accessLevelFilter;
      }

      if (hasOverrideFilter) {
        q.accessOverride = { $ne: null };
      }

      if (includeDeleted) {
        q.includeDeleted = true;
      }

      const result = await service.find({
        query: q,
      });

      setUsers(result.data);
      setTotal(result.total);
    } catch (e) {
      console.log('error', e);
      alert('there was an issue loading the users');
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(0);
    getUsers();
  };
  const navigateToPage = (p = 0, kw) => {
    if (p * limit > total || p * limit < 0) return;
    let url = `/admin/users?page=${p}`;
    if (kw) {
      url += `&keywords=${kw}`;
    }
    if (accessLevelFilter) {
      url += `&accessLevel=${accessLevelFilter}`;
    }
    navigate(url);
    setPage(p);
  };

  const onNewUser = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  useEffect(() => {
    getUsers();
  }, [page]);

  useEffect(() => {
    setPage(0);
    getUsers();
  }, [roleFilter]);

  useEffect(() => {
    setPage(0);
    getUsers();
  }, [accessLevelFilter]);

  useEffect(() => {
    setPage(0);
    getUsers();
  }, [hasOverrideFilter]);

  useEffect(() => {
    getUsers();
  }, [includeDeleted]);

  return (
    <Page title="Users">

      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">

        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Users' }]} />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
        </div>
      </div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Users (
          {total}
          {' '}
          Total)
        </h3>
        <EntitySearchForm onSearch={onSearch} setKeywords={setKeywords} keywords={keywords} />
        <div className="flex items-center space-x-4">
          <label className="flex items-center text-sm text-gray-700">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            Show deleted users
          </label>
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>
      <div>
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">Select a tab</label>
          <select onChange={(e) => setRoleFilter(e.target.value)} value={roleFilter} id="tabs" name="tabs" className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md">
            {roleList.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200 flex items-center justify-between gap-4">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {roleList.map((s) => {
                let styles = 'capitalize border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm';
                if (roleFilter === s) {
                  styles = 'capitalize border-gray-500 text-gray-600 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm';
                }
                return <a href="#" onClick={() => setRoleFilter(s)} className={styles} aria-current="page" key={s}>{s}</a>;
              })}
            </nav>
            <div className="flex flex-wrap items-center gap-2 pb-2">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider mr-1">Access</span>
              <button
                type="button"
                onClick={() => setAccessLevelFilter('')}
                className={`px-2 py-0.5 text-xs rounded-full ${
                  !accessLevelFilter
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              {ACCESS_LEVELS.map((lvl) => (
                <button
                  key={lvl}
                  type="button"
                  onClick={() => setAccessLevelFilter(lvl)}
                  className={`px-2 py-0.5 text-xs rounded-full capitalize ${
                    accessLevelFilter === lvl
                      ? 'bg-gray-900 text-white'
                      : `${ACCESS_BADGE_COLORS[lvl] || 'bg-gray-100 text-gray-700'} hover:opacity-80`
                  }`}
                >
                  {lvl}
                </button>
              ))}
              <label className="ml-3 flex items-center text-sm text-gray-700 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={hasOverrideFilter}
                  onChange={(e) => setHasOverrideFilter(e.target.checked)}
                  className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                Has comp / override
              </label>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <b>Name</b>
                      <br />
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Access
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signed Up
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((u) => {
                    const overrideActive = isOverrideActive(u.accessOverride);
                    return (
                      <tr className="hover:bg-gray-100 cursor-pointer" key={u.id} onClick={() => onNewUser(u)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <UserAvatar user={u} size="md" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {u.firstName}
                                &nbsp;
                                {u.lastName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {u.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              u.role === 'admin' ? 'bg-red-100 text-red-800' :
                              u.role === 'industry' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {u.role}
                            </span>
                            {u.deletedAt && (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-700 text-white">
                                Deleted
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <TierBadge tier={u.accessLevel} />
                            {overrideActive && (
                              <span
                                title={`Comped to ${u.accessOverride.level}${u.accessOverride.source ? ` (${u.accessOverride.source})` : ''}${u.accessOverride.expiresAt ? `, expires ${format(new Date(u.accessOverride.expiresAt), 'MMM d, yyyy')}` : ''}`}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700"
                              >
                                ★ {u.accessOverride.level}
                              </span>
                            )}
                            {u.accessOverride && !overrideActive && (
                              <span
                                title="Override has expired"
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
                              >
                                expired comp
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{format(new Date(u.createdAt), 'MMM d, yyyy')}</div>
                          <div className="text-xs text-gray-500">{formatDistance(new Date(u.createdAt), new Date(), { addSuffix: true })}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link to={`/admin/users/${u.id}/edit`} className="text-red-600 hover:text-red-900">Edit</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
            </div>
          </div>
        </div>
      </div>

    </Page>
  );
}
